import React, { useEffect, useState } from 'react';
import {
    Button,
    InputControl,
    LoaderMi,
    Link,
    FieldsetControl,
    CheckboxControl,
    Row,
    Col,
    Container,
} from '@design-system-etat/dsfr-react';
import { useDispatch, useSelector } from 'react-redux';
import { getAPI, reinitialiseAPI, postAPI } from 'store/apiReducer';
import * as PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import DateInput from 'components/Commun/Form/Date/DateInput';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_GET_REFERENTIEL_ELEMENTS_ARME,
    API_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA,
    CONTEXT_GET_REFERENTIEL_ELEMENTS_ARME,
    CONTEXT_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA,
    CONTEXT_VERIFIER_EXISTENCE_DOUBLON,
    API_VERIFIER_EXISTENCE_DOUBLON,
} from 'constants/apiUrls.constants';
import { REGEX_RGA } from 'constants/contraintes.constants';
import TableauRga from '../tableauRga/TableauRga';
import BoutonEtape from '../../form/boutonsEtape/BoutonEtape';
import ModaleArmeNonSoumiseADeclaration2011 from './components/ModaleArmeNonSoumiseADeclaration2011';
import { comparerDeuxDates } from '../../../utils/utils';
import { getDateFRFromYYYYMMDDHHMMSS } from '../../Commun/Form/Date/dateTranformation';
import InfoChampsObligatoire from '../../form/infoObligatoire/InfoChampsObligatoire';

const ModifierArmeElementsArme = ({
    dataArmeElementsArme,
    etapePrecedente,
    enregistrerData,
    etapeSuivante,
    modaleArmeAvant2011,
    recuperationArmeEtElementsExistant,
}) => {
    const dispatch = useDispatch();
    const { isLoading: isLoadingReferentielElementArme, data: dataReferentielElementArme } =
        useSelector((state) => state.referentiel.elementsArme);
    const {
        isLoading: isLoadingGdr,
        data: gdr,
        erreur: erreurGdr,
        message: messageGdr,
    } = useSelector((state) => state.armes.recupererGdrParNumeroRga);

    const { isLoading: isLoadingDoublon, data: dataDoublon } = useSelector(
        (state) => state.armes.verifierDoublonArme,
    );

    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: dataArmeElementsArme,
    });

    const {
        handleSubmit,
        register,
        watch,
        formState,
        setError,
        clearErrors,
        setValue,
        getValues,
        reset,
    } = hookForm;
    const [isNumeroRgaValide, setIsNumeroRgaValide] = useState(false);
    const [needDateAcquisition, setNeedDateAcquisition] = useState(false);
    const [
        displayModaleArmeNonSoumiseADeclaration2011,
        setDisplayModaleArmeNonSoumiseADeclaration2011,
    ] = useState(false);
    const { REACT_APP_RGA_URL } = process.env;
    const [dataElementArmeToDisplay, setDataElementArmeToDisplay] = useState(null);

    const recupererArmeRGA = (RgaId) => {
        dispatch(
            getAPI(
                usagerClientReducer,
                API_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA + RgaId,
                CONTEXT_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA,
            ),
        );
    };

    useEffect(() => {
        dispatch(reinitialiseAPI(CONTEXT_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA));
        dispatch(
            getAPI(
                usagerClientReducer,
                API_GET_REFERENTIEL_ELEMENTS_ARME,
                CONTEXT_GET_REFERENTIEL_ELEMENTS_ARME,
            ),
        );
        if (dataArmeElementsArme && dataArmeElementsArme.nouveauNumeroRga) {
            dispatch(
                getAPI(
                    usagerClientReducer,
                    API_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA + dataArmeElementsArme.nouveauNumeroRga,
                    CONTEXT_RECUPERER_ARME_GDR_AVEC_NUMERO_RGA,
                ),
            );
        }
    }, []);

    const verifierDoublonArme = () => {
        const verifierExistenceDoublonInput = {
            numeroRga: watch('nouveauNumeroRga'),
            numeroSerie: !watch('noNumeroSerie') ? watch('nouveauNumeroSerie') : null,
        };

        dispatch(
            postAPI(
                usagerClientReducer,
                API_VERIFIER_EXISTENCE_DOUBLON,
                verifierExistenceDoublonInput,
                CONTEXT_VERIFIER_EXISTENCE_DOUBLON,
            ),
        );
    };

    useEffect(() => {
        if (
            watch('nouveauNumeroRga') &&
            REGEX_RGA.exec(watch('nouveauNumeroRga')) &&
            watch('nouveauNumeroRga').length === 5
        ) {
            recupererArmeRGA(watch('nouveauNumeroRga'));
        }

        if (getValues('nouveauNumeroSerie')) {
            verifierDoublonArme();
        }
    }, [watch('nouveauNumeroRga')]);

    useEffect(() => {
        if (watch('nouveauNumeroRga') && watch('nouveauNumeroRga').length === 5) {
            if (!REGEX_RGA.exec(watch('nouveauNumeroRga')) || erreurGdr) {
                setError('nouveauNumeroRga', {
                    type: 'manual',
                    message: messageGdr ?? 'N°RGA non valide, veuillez saisir un n°RGA valide',
                });
            }
            setIsNumeroRgaValide(!erreurGdr && gdr);
        } else {
            clearErrors('nouveauNumeroRga');
            setIsNumeroRgaValide(false);
        }
    }, [isLoadingGdr, erreurGdr, messageGdr, gdr, watch('nouveauNumeroRga')]);

    useEffect(() => {
        if (dataReferentielElementArme && recuperationArmeEtElementsExistant) {
            let elementArmeTmp;
            const { gdrRga, dateNeutralisation, lieuNeutralisation, numeroCarcasse, neutralisee } =
                dataArmeElementsArme;
            const { numeroRGA } = gdrRga || {};

            const infoExistantes = {
                nouveauNumeroRga: numeroRGA,
                nouveauNumeroSerie: numeroCarcasse,
                dateNeutralisationArme: dateNeutralisation
                    ? getDateFRFromYYYYMMDDHHMMSS(dateNeutralisation)
                    : null,
                lieuNeutralisationArme: lieuNeutralisation,
                isNeutralisee: neutralisee,
                noNumeroSerie: !numeroCarcasse,
            };

            recupererArmeRGA(numeroRGA);

            dataReferentielElementArme.typeElementArmeReferentielArme.forEach((elementArme) => {
                elementArmeTmp = elementArme;
                const elementExistant = Object.values(dataArmeElementsArme.elementsArme).find(
                    (element) => element.elementArmeReferentiel.id === elementArme.id,
                );
                if (elementExistant) {
                    elementArmeTmp.numSerie = elementExistant.numSerie;
                    const elemId = `elementArme-${elementExistant.elementArmeReferentiel.id}`;
                    infoExistantes[elemId] = elementExistant.numSerie;
                }
            });

            reset(infoExistantes);
        }
        setDataElementArmeToDisplay(dataReferentielElementArme);
    }, [dataReferentielElementArme, dataArmeElementsArme]);

    useEffect(() => {
        const { casParticulierNonSoumisADeclarationAvant2011 } = gdr || {};
        if (gdr && casParticulierNonSoumisADeclarationAvant2011 && modaleArmeAvant2011) {
            setNeedDateAcquisition(true);
        } else {
            setNeedDateAcquisition(false);
        }
    }, [gdr]);

    useEffect(() => {
        if (!watch('isNeutralisee')) {
            setValue('dateNeutralisationArme', null);
            setValue('lieuNeutralisationArme', null);
            clearErrors('dateNeutralisationArme');
            clearErrors('lieuNeutralisationArme');
        }
    }, [watch('isNeutralisee')]);

    const copierNumeroSerie = (e, nomInput) => {
        setValue(`elementArme-${nomInput}`, getValues('nouveauNumeroSerie'));
    };

    const disabledBoutonSuivant = () => {
        if (getValues('nouveauNumeroSerie') === '') {
            setValue('nouveauNumeroSerie', null);
        }
        return !(
            Object.keys(formState.errors).length <= 0 &&
            isNumeroRgaValide &&
            (watch('noNumeroSerie') ||
                (!watch('noNumeroSerie') &&
                    watch('nouveauNumeroSerie') &&
                    dataDoublon &&
                    !dataDoublon.doublon)) &&
            (!watch('isNeutralisee') ||
                (watch('isNeutralisee') &&
                    watch('dateNeutralisationArme') &&
                    watch('lieuNeutralisationArme'))) &&
            (!needDateAcquisition || (needDateAcquisition && watch('dateAcquisitionArme')))
        );
    };

    const ouvrirModaleArmeNonSoumiseADeclaration = () => {
        setDisplayModaleArmeNonSoumiseADeclaration2011(true);
    };

    const fermerModaleArmeNonSoumiseADeclaration = () => {
        setDisplayModaleArmeNonSoumiseADeclaration2011(false);
    };

    const finEtape = () => {
        etapeSuivante();
    };

    const onSubmit = (dataForm) => {
        // Permet de passer tous les champs elementArme qui sont vides à null
        const formatData = Object.keys(dataForm).reduce((val, field) => {
            const value = { ...val };
            if (dataForm[field] === '' && field.startsWith('elementArme-')) {
                value[field] = null;
            } else {
                value[field] = dataForm[field];
            }
            return value;
        }, {});
        enregistrerData(formatData);

        let isAvantPremierDecembre2011 = false;
        if (dataForm.dateAcquisitionArme) {
            isAvantPremierDecembre2011 = comparerDeuxDates(
                '01/12/2011',
                dataForm.dateAcquisitionArme,
            );
        }
        if (isAvantPremierDecembre2011) {
            ouvrirModaleArmeNonSoumiseADeclaration();
        } else {
            finEtape();
        }
    };

    const noNumeroSerie = watch('noNumeroSerie');
    const nouveauNumeroSerie = watch('nouveauNumeroSerie');
    const hasDoublon = dataDoublon?.doublon;
    const isValidSerie = !isLoadingDoublon && dataDoublon && !hasDoublon && nouveauNumeroSerie;

    return (
        <Container>
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <InfoChampsObligatoire />

                    <Row>
                        <Col col={12}>
                            <h2>Information de l&apos;arme</h2>
                        </Col>
                    </Row>

                    <FieldsetControl
                        id="saisieNouveauNumRga"
                        legend="Veuillez saisir le numéro RGA de votre arme :"
                    >
                        <Row gutters verticalAlign="middle">
                            <Col col={12} md={6}>
                                <InputControl
                                    id="nouveauNumeroRga"
                                    label="Numéro RGA *"
                                    name="nouveauNumeroRga"
                                    type="text"
                                    maxLength="5"
                                    uppercase
                                    {...register('nouveauNumeroRga', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                    required
                                    state={formState.errors?.nouveauNumeroRga ? 'error' : null}
                                    message={formState.errors?.nouveauNumeroRga?.message}
                                    valid={
                                        isNumeroRgaValide
                                            ? { message: 'Le n° RGA est valide' }
                                            : null
                                    }
                                />
                            </Col>
                            <Col col={12} md={6}>
                                <Link
                                    href={REACT_APP_RGA_URL}
                                    icon="fr-fi-arrow-right-line"
                                    target="_blank"
                                >
                                    Rechercher sur le RGA
                                </Link>
                            </Col>
                        </Row>
                        <>
                            {gdr && (
                                <Row gutters>
                                    <Col col={12}>
                                        <TableauRga dataRga={gdr} />
                                    </Col>
                                </Row>
                            )}
                        </>
                    </FieldsetControl>

                    <FieldsetControl
                        id="saisieNumSerie"
                        legend="Veuillez saisir le numéro de série de votre arme :"
                    >
                        <Row gutters verticalAlign="bottom">
                            <Col col={12} lg={6}>
                                <InputControl
                                    label={`Numéro de série${!noNumeroSerie ? ' *' : ''}`}
                                    id="numeroSerie"
                                    hintText="Numéro de carcasse / bascule / boîte de culasse / partie inférieur de la boîte de culasse (lower)"
                                    name="nouveauNumeroSerie"
                                    type="text"
                                    uppercase
                                    {...register('nouveauNumeroSerie', {
                                        required: !noNumeroSerie,
                                    })}
                                    onBlur={nouveauNumeroSerie ? verifierDoublonArme : undefined}
                                    disabled={noNumeroSerie}
                                    required={!noNumeroSerie}
                                    state={hasDoublon && nouveauNumeroSerie ? 'error' : null}
                                    message={
                                        hasDoublon && nouveauNumeroSerie
                                            ? 'Ce numéro de série est déjà utilisé'
                                            : null
                                    }
                                    valid={
                                        isValidSerie
                                            ? { message: 'Le n° de série est valide' }
                                            : null
                                    }
                                />
                            </Col>
                            <Col col={12} lg={6}>
                                <CheckboxControl
                                    id="noNumeroSerie"
                                    name="noNumeroSerie"
                                    label="Cocher cette case s'il n'y a pas de numéro de série"
                                    {...register('noNumeroSerie')}
                                    state={formState.errors?.noNumeroSerie ? 'error' : null}
                                    message={formState.errors?.noNumeroSerie?.message}
                                    disabled={watch('nouveauNumeroSerie')}
                                />
                            </Col>
                        </Row>
                    </FieldsetControl>

                    {needDateAcquisition && (
                        <FieldsetControl
                            id="saisieDateAcquisition"
                            legend="Veuillez saisir la date d'acquisition de votre arme :"
                        >
                            <Row>
                                <Col col={12} lg={6}>
                                    <DateInput
                                        label="Date d'acquisition"
                                        id="dateAcquisitionArme"
                                        name="dateAcquisitionArme"
                                        required
                                        noFuture
                                    />
                                </Col>
                            </Row>
                        </FieldsetControl>
                    )}

                    <FieldsetControl
                        id="saisieArmeNeutralisee"
                        legend="Si votre arme est neutralisée, veuillez saisir les informations suivantes :"
                    >
                        <Row gutters>
                            <Col col={12}>
                                <CheckboxControl
                                    id="isNeutralisee"
                                    name="isNeutralisee"
                                    label="Cocher cette case si l'arme est neutralisée"
                                    {...register('isNeutralisee')}
                                    value
                                />
                            </Col>
                            <Col col={12} lg={6}>
                                <DateInput
                                    label="Date de neutralisation"
                                    id="dateNeutralisationArme"
                                    name="dateNeutralisationArme"
                                    required={
                                        watch('isNeutralisee') ? 'Ce champs est requis' : null
                                    }
                                    noFuture={watch('isNeutralisee')}
                                    disabled={!watch('isNeutralisee')}
                                />
                            </Col>
                            <Col col={12} lg={6}>
                                <InputControl
                                    label={`Lieu de neutralisation${
                                        watch('isNeutralisee') ? ' *' : ''
                                    }`}
                                    id="lieuNeutralisationArme"
                                    name="lieuNeutralisationArme"
                                    type="text"
                                    uppercase
                                    {...register('lieuNeutralisationArme', {
                                        required: watch('isNeutralisee')
                                            ? 'Ce champs est requis'
                                            : null,
                                    })}
                                    state={
                                        formState.errors?.lieuNeutralisationArme ? 'error' : null
                                    }
                                    message={formState.errors?.lieuNeutralisationArme?.message}
                                    required={watch('isNeutralisee')}
                                    disabled={!watch('isNeutralisee')}
                                />
                            </Col>
                        </Row>
                    </FieldsetControl>

                    <Row className="margin-top-lg">
                        <Col col={12}>
                            <h2>Information des éléments de l&apos;arme</h2>
                        </Col>
                    </Row>

                    {isLoadingReferentielElementArme && <LoaderMi taille="md" />}
                    <FieldsetControl
                        id="saisieNumeroSerieElementsArme"
                        legend="Veuillez saisir le numéro de série des éléments essentiels de l'arme :"
                        hintText="Remplir les champs correspondants aux éléments constitutifs de votre arme"
                    >
                        <Row gutters>
                            {dataReferentielElementArme &&
                                dataElementArmeToDisplay &&
                                dataElementArmeToDisplay.typeElementArmeReferentielArme.map(
                                    (element) => {
                                        return (
                                            <Col col={12} md={6}>
                                                <Row gutters verticalAlign="bottom">
                                                    <Col col={10}>
                                                        <InputControl
                                                            label={element.libelle}
                                                            id={`elementArme-${element.id}`}
                                                            name={`elementArme-${element.id}`}
                                                            type="text"
                                                            uppercase
                                                            {...register(
                                                                `elementArme-${element.id}`,
                                                            )}
                                                        />
                                                    </Col>
                                                    {!watch('noNumeroSerie') && (
                                                        <Col col={2}>
                                                            <Button
                                                                type="button"
                                                                onClick={(e) =>
                                                                    copierNumeroSerie(e, element.id)
                                                                }
                                                                name={`boutonCopierNumeroSerie-${element.libelle}`}
                                                                title={`Copier le numéro de série dans le champ ${element.libelle}`}
                                                                icon="fr-fi-external-link-line"
                                                                size="sm"
                                                                variant="primary"
                                                            />
                                                        </Col>
                                                    )}
                                                </Row>
                                            </Col>
                                        );
                                    },
                                )}
                        </Row>
                    </FieldsetControl>

                    <BoutonEtape
                        actionPrecedente={etapePrecedente}
                        labelBtnPrecedent="Annuler"
                        labelBtnSuivant="Etape suivante"
                        nameBtnSuivant="boutonEtapeSuivanteModificationArme"
                        btnSuivantDisabled={disabledBoutonSuivant()}
                    />
                    {displayModaleArmeNonSoumiseADeclaration2011 && (
                        <ModaleArmeNonSoumiseADeclaration2011
                            etapeSuivante={finEtape}
                            retourArriere={etapePrecedente}
                            fermerModale={fermerModaleArmeNonSoumiseADeclaration}
                        />
                    )}
                </form>
            </FormProvider>
        </Container>
    );
};

ModifierArmeElementsArme.propTypes = {
    dataArmeElementsArme: PropTypes.shape({
        nouveauNumeroRga: PropTypes.string,
        elementsArme: PropTypes.array,
        neutralisee: PropTypes.bool,
        dateNeutralisation: PropTypes.string,
        lieuNeutralisation: PropTypes.string,
        numeroCarcasse: PropTypes.string,
        numeroRga: PropTypes.string,
        gdrRga: PropTypes.shape({
            numeroRGA: PropTypes.string,
        }),
    }),
    etapePrecedente: PropTypes.func.isRequired,
    enregistrerData: PropTypes.func.isRequired,
    etapeSuivante: PropTypes.func.isRequired,
    modaleArmeAvant2011: PropTypes.bool,
    recuperationArmeEtElementsExistant: PropTypes.bool,
};

ModifierArmeElementsArme.defaultProps = {
    dataArmeElementsArme: null,
    modaleArmeAvant2011: false,
    recuperationArmeEtElementsExistant: false,
};

export default ModifierArmeElementsArme;
